import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageNavigation } from './usePageNavigation';
import { useZoomControls } from './useZoomControls';
import { usePdfViewer } from './usePdfViewer';
import { usePdfHighlight } from './usePdfHighlight';
import { useFeedback } from './useFeedback';
import { useInsuranceHikiukeSession } from './useInsuranceHikiukeSession';
import { useJumpButtonGroup } from './useJumpButtonGroup';
import { usePdfKeywordSearch } from '../../usePdfKeywordSearch';

/**
 * 引受目安詳細画面のメインカスタムフック
 * 
 * 各サブフックを統合してオーケストレーションを行う。
 * このフックは以下の責務を持つ:
 * - 各サブフックの初期化と連携
 * - PDF読み込みのトリガー管理
 * - react-pdfコールバックのラッピング
 * - 画面遷移とリトライ処理
 * 
 * @returns {Object} 引受目安詳細画面に必要な状態とハンドラ
 * 
 * @returns {boolean} returns.loading - PDF読み込み中かどうか
 * @returns {number} returns.zoomLevel - 現在のズームレベル（%）
 * @returns {number} returns.currentPage - 現在のページ番号
 * @returns {number} returns.totalPages - 総ページ数
 * @returns {boolean} returns.showErrorPage - エラーページ表示フラグ
 * @returns {ArrayBuffer|null} returns.pdfFile - PDFファイルデータ
 * @returns {boolean} returns.isFeedbackSubmitting - フィードバック送信中フラグ
 * @returns {string} returns.errorMessage - エラーメッセージ
 * @returns {boolean} returns.isLandscape - ページ横向きフラグ
 * @returns {boolean} returns.isRendering - レンダリング中フラグ
 * @returns {Object} returns.documentInfo - ドキュメント情報
 * @returns {boolean} returns.isDocInfoAccordionOpen - ドキュメント情報アコーディオン開閉状態
 * @returns {boolean} returns.isMobile - モバイル表示かどうか
 * @returns {string|null} returns.currentPopup - 現在表示中のポップアップ
 * @returns {string|null} returns.currentQueryId - 現在のクエリID
 * @returns {string} returns.activeJumpButton - アクティブなジャンプボタン
 * @returns {boolean} returns.canJumpDisease - 該当傷病へジャンプ可能か
 * @returns {boolean} returns.canJumpContents - 目次へジャンプ可能か
 * @returns {boolean} returns.canJumpLegend - 凡例へジャンプ可能か
 * @returns {boolean} returns.canJumpAppendix - 別表へジャンプ可能か
 * @returns {Array|null} returns.highlightsPolygon - ハイライトポリゴン座標
 * @returns {Object} returns.pdfOptions - react-pdf用設定オブジェクト
 * @returns {React.RefObject} returns.highlightCanvasRef - ハイライトCanvas要素のref
 * @returns {React.RefObject} returns.pdfContainerRef - PDFコンテナ要素のref
 * @returns {Function} returns.handleZoomIn - ズームインハンドラ
 * @returns {Function} returns.handleZoomOut - ズームアウトハンドラ
 * @returns {Function} returns.handlePageChange - ページ変更ハンドラ
 * @returns {Function} returns.handlePrevPage - 前ページへ移動ハンドラ
 * @returns {Function} returns.handleNextPage - 次ページへ移動ハンドラ
 * @returns {Function} returns.handleChangePageInput - ページ入力変更ハンドラ
 * @returns {Function} returns.handlePageInputBlur - ページ入力フォーカスアウトハンドラ
 * @returns {Function} returns.handleRetry - リトライハンドラ
 * @returns {Function} returns.handleBackToSearch - 検索画面に戻るハンドラ
 * @returns {Function} returns.toggleDocInfoAccordion - ドキュメント情報アコーディオン切替ハンドラ
 * @returns {Function} returns.handleClose - ウィンドウを閉じるハンドラ
 * @returns {Function} returns.handleFeedbackClose - フィードバックポップアップを閉じるハンドラ
 * @returns {Function} returns.handleGoodFeedback - 良いフィードバックハンドラ
 * @returns {Function} returns.handleBadFeedback - 悪いフィードバックハンドラ
 * @returns {Function} returns.handleFeedbackSubmit - フィードバック送信ハンドラ
 * @returns {Function} returns.handleFeedbackBack - フィードバック戻るハンドラ
 * @returns {Function} returns.handleJumpDisease - 該当傷病へジャンプハンドラ
 * @returns {Function} returns.handleJumpContents - 目次へジャンプハンドラ
 * @returns {Function} returns.handleJumpLegends - 凡例へジャンプハンドラ
 * @returns {Function} returns.handleJumpAppendix - 別表へジャンプハンドラ
 * @returns {Function} returns.onDocumentLoadSuccess - ドキュメント読み込み成功コールバック
 * @returns {Function} returns.onDocumentLoadError - ドキュメント読み込みエラーコールバック
 * @returns {Function} returns.onPageLoadSuccess - ページ読み込み成功コールバック
 * @returns {Function} returns.onPageLoadError - ページ読み込みエラーコールバック
 * @returns {Function} returns.setCurrentPage - 現在ページ設定関数
 */
export const useInsuranceHikiukeDetail = () => {
  const navigate = useNavigate();

  /**
   * サブフック: ズームコントロール
   * ズームイン・ズームアウト操作を提供
   */
  const zoomControls = useZoomControls();
  const {
    zoomLevel,
    handleZoomIn,
    handleZoomOut,
  } = zoomControls;

  /**
   * サブフック: ページナビゲーション
   * ページ移動、ページ入力を提供
   */
  const navigation = usePageNavigation();
  const {
    currentPage,
    totalPages,
    isRendering,
    setCurrentPage,
    setTotalPages,
    setIsRendering,
    handlePageChange,
    handlePrevPage,
    handleNextPage,
    pageInputValue,
    handleChangePageInput,
    handlePageInputKeyDown,
    handlePageInputBlur,
    jumpToPage,
    isJumpPageValid,
  } = navigation;

  /**
   * サブフック: PDFビューワー
   * PDF読み込み、表示、エラー処理を提供
   */
  const pdfViewer = usePdfViewer({
    setIsRendering,
  });
  const {
    loading,
    pdfFile,
    pdfDocument,
    showErrorPage,
    errorMessage,
    isLandscape,
    pageInfo,
    pdfOptions,
    loadPdfDocument,
    onDocumentLoadSuccess: pdfDocumentLoadSuccess,
    onDocumentLoadError,
    onPageLoadSuccess: pdfPageLoadSuccess,
    onPageLoadError: pdfPageLoadError,
    resetPdfState,
    handleError,
  } = pdfViewer;

  /**
   * サブフック: PDFハイライト
   * 検索結果箇所のハイライト表示を提供
   */
  const highlight = usePdfHighlight({
    pageInfo,
    currentPage,
    zoomLevel,
  });
  const {
    highlightsPolygon,
    highlightCanvasRef,
    pdfContainerRef,
    initializeHighlight,
  } = highlight;

  /**
   * サブフック: セッション管理
   * セッションストレージからのデータ復元を提供
   */
  const session = useInsuranceHikiukeSession({
    onError: handleError,
    setCurrentPage,
    initializeHighlight,
  });
  const {
    detailResult,
    blobName,
    currentQueryId,
    documentInfo,
    isMobile,
    isDocInfoAccordionOpen,
    toggleDocInfoAccordion,
    groupedResults = null,
    activeCompany = null,
    activeCompanyResults = [],
    selectedDiseaseByCompany = {},
    activateCompany = null,
    activateDisease = null,
  } = session;

  /**
   * サブフック: フィードバック
   * ユーザーフィードバック機能を提供
   */
  const feedback = useFeedback(currentQueryId, detailResult?.fromDocumentCatalog);
  const {
    currentPopup,
    isFeedbackSubmitting,
    handleFeedbackClose,
    handleGoodFeedback,
    handleBadFeedback,
    handleFeedbackSubmit,
    handleFeedbackBack,
  } = feedback;

  /**
   * サブフック: ジャンプボタングループ
   * 該当傷病、目次、凡例、別表へのジャンプを提供
   */
  /**
   * 現在選択中の傷病キー
   * 会社タブごとの選択状態がない場合は先頭の傷病を使用する
   */
  const activeDiseaseKey = useMemo(() => {
    if (!activeCompany) return null;
    return selectedDiseaseByCompany[activeCompany] ?? activeCompanyResults[0]?._key ?? null;
  }, [activeCompany, selectedDiseaseByCompany, activeCompanyResults]);

  /**
   * 会社・傷病ペア単位で状態を保存するためのキー
   * 複数PDF表示に未対応の古いデータではqueryIdやblobNameを代替キーとして使用する
   */
  const activePairKey = useMemo(() => {
    if (activeCompany && activeDiseaseKey) {
      return `${activeCompany}::${activeDiseaseKey}`;
    }
    if (currentQueryId) return `query::${currentQueryId}`;
    if (blobName) return `blob::${blobName}`;
    return null;
  }, [activeCompany, activeDiseaseKey, currentQueryId, blobName]);

  const jumpButtonGroup = useJumpButtonGroup({
    detailResult,
    jumpToPage,
    isJumpPageValid,
    activePairKey,
    currentPage,
  });
  const {
    activeJumpButton,
    canJumpDisease,
    canJumpContents,
    canJumpLegend,
    canJumpAppendix,
    handleJumpDisease: baseHandleJumpDisease,
    handleJumpContents: baseHandleJumpContents,
    handleJumpLegends: baseHandleJumpLegends,
    handleJumpAppendix: baseHandleJumpAppendix,
  } = jumpButtonGroup;

  /**
   * 会社・傷病ペアごとの表示ページ状態
   * key: ペアキー、value: 最後に表示していたページ番号
   */
  const pageStateByPairRef = useRef({});

  /**
   * 会社・傷病ペアごとのスクロール位置
   * PDFのズームやページ内移動位置をタブ切り替え後に復元するために使用
   */
  const scrollStateByPairRef = useRef({});

  /**
   * 検索スコープごとの検索文字列
   * 会社・傷病・ジャンプボタンの組み合わせごとに直近の検索語を保持する
   */
  const searchTextByScopeRef = useRef({});

  /**
   * 直前の会社・傷病ペアキー
   * ペア切り替え直後のページ保存や検索復元を制御するために使用
   */
  const previousPairKeyRef = useRef(null);

  /**
   * 直前の検索スコープキー
   * ジャンプボタン切り替え時に検索語を復元するか判定するために使用
   */
  const previousSearchScopeKeyRef = useRef(null);

  /**
   * スクロール位置の復元待ちペアキー
   * PDFページの読み込み完了後にスクロールを復元するために使用
   */
  const pendingScrollRestorePairRef = useRef(null);

  /**
   * 自動再検索の予約情報
   * PDFドキュメントの読み込み完了後に、保存済み検索語で検索を再実行する
   */
  const pendingAutoSearchRef = useRef(null);

  /**
   * 読み込み完了済みの会社・傷病ペアキー
   * 自動再検索をPDF読み込み完了後に限定するために使用
   */
  const loadedDocumentPairRef = useRef(null);

  /**
   * PDFスクロールコンテナのref
   * 会社・傷病ペアごとのスクロール位置保存と復元に使用
   */
  const pdfScrollContainerRef = useRef(null);

  /**
   * 会社・傷病ペアごとの表示ページを保存する
   *
   * @param {string} pairKey - 会社・傷病ペアキー
   * @param {number} pageNumber - 保存するページ番号
   */
  const savePairPageState = useCallback((pairKey, pageNumber) => {
    if (!pairKey) return;
    if (!Number.isFinite(pageNumber) || pageNumber < 1) return;
    pageStateByPairRef.current[pairKey] = pageNumber;
  }, []);

  /**
   * 会社・傷病ペアごとのスクロール位置を保存する
   *
   * @param {string} pairKey - 会社・傷病ペアキー
   */
  const savePairScrollState = useCallback((pairKey) => {
    if (!pairKey) return;
    const container = pdfScrollContainerRef.current;
    if (!container) return;

    scrollStateByPairRef.current[pairKey] = {
      top: container.scrollTop,
      left: container.scrollLeft,
    };
  }, []);

  /**
   * 現在選択中の会社・傷病ペアのページ番号とスクロール位置を保存する
   */
  const saveActivePairState = useCallback(() => {
    if (!activePairKey) return;
    savePairPageState(activePairKey, currentPage);
    savePairScrollState(activePairKey);
  }, [activePairKey, currentPage, savePairPageState, savePairScrollState]);

  /**
   * 会社・傷病ペアごとのスクロール位置を復元する
   *
   * @param {string} pairKey - 会社・傷病ペアキー
   */
  const restorePairScrollState = useCallback((pairKey) => {
    const container = pdfScrollContainerRef.current;
    if (!container) return;

    const savedScroll = scrollStateByPairRef.current[pairKey];
    const top = savedScroll?.top ?? 0;
    const left = savedScroll?.left ?? 0;
    container.scrollTo(left, top);
  }, []);
  // モバイル用検索パネルの開閉状態（UI状態のため検索ロジックフックには持たせない）
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  /**
   * サブフック: PDFキーワード検索
   * テキスト検索・ページナビゲーション・ハイライトを提供
   */
  const keywordSearch = usePdfKeywordSearch({
    pdfDocument,
    currentPage,
    setCurrentPage,
    containerRef: pdfContainerRef,
  });
  const {
    searchText,
    setSearchText,
    searchResults,
    currentSearchIndex,
    isSearching,
    searchError,
    handleSearch,
    handleNextSearchResult,
    handlePrevSearchResult,
    handleClearSearch: baseHandleClearSearch,
    customTextRenderer,
    onGetTextSuccess,
    onTextLayerRenderSuccess,
  } = keywordSearch;

  /**
   * 検索文字列を保存するスコープキー
   * 会社・傷病ペアとジャンプボタンの組み合わせで検索状態を分離する
   */
  const activeSearchScopeKey = useMemo(() => {
    if (!activePairKey || !activeJumpButton) return null;
    return `${activePairKey}::${activeJumpButton}`;
  }, [activePairKey, activeJumpButton]);

  /**
   * 検索スコープごとの検索文字列を保存する
   * 空文字の場合は保存済み検索語を削除する
   *
   * @param {string} scopeKey - 検索スコープキー
   * @param {string} queryText - 検索文字列
   */
  const saveSearchTextForScope = useCallback((scopeKey, queryText) => {
    if (!scopeKey) return;
    const normalized = (queryText || '').trim();
    if (!normalized) {
      delete searchTextByScopeRef.current[scopeKey];
      return;
    }
    searchTextByScopeRef.current[scopeKey] = normalized;
  }, []);

  /**
   * 検索文字列をスコープ単位で保存してから検索を実行する
   */
  const handleSearchWithPersist = useCallback(() => {
    saveSearchTextForScope(activeSearchScopeKey, searchText);
    handleSearch();
  }, [activeSearchScopeKey, searchText, saveSearchTextForScope, handleSearch]);

  /**
   * ユーザー操作による検索クリア処理
   * 現在スコープの保存済み検索語と自動再検索予約も削除する
   */
  const handleClearSearchByUser = useCallback(() => {
    if (activeSearchScopeKey) {
      delete searchTextByScopeRef.current[activeSearchScopeKey];
    }
    pendingAutoSearchRef.current = null;
    baseHandleClearSearch();
  }, [activeSearchScopeKey, baseHandleClearSearch]);

  /**
   * ジャンプ操作前に検索状態をクリアする
   * ジャンプ後のハイライトや検索パネル表示が残らないようにする
   */
  const clearSearchForJump = useCallback(() => {
    pendingAutoSearchRef.current = null;
    baseHandleClearSearch();
    setIsSearchOpen(false);
  }, [baseHandleClearSearch]);

  /**
   * 検索状態をクリアして該当傷病へジャンプする
   */
  const handleJumpDisease = useCallback(() => {
    clearSearchForJump();
    baseHandleJumpDisease();
  }, [baseHandleJumpDisease, clearSearchForJump]);

  /**
   * 検索状態をクリアして目次へジャンプする
   */
  const handleJumpContents = useCallback(() => {
    clearSearchForJump();
    baseHandleJumpContents();
  }, [baseHandleJumpContents, clearSearchForJump]);

  /**
   * 検索状態をクリアして凡例へジャンプする
   */
  const handleJumpLegends = useCallback(() => {
    clearSearchForJump();
    baseHandleJumpLegends();
  }, [baseHandleJumpLegends, clearSearchForJump]);

  /**
   * 検索状態をクリアして別表へジャンプする
   */
  const handleJumpAppendix = useCallback(() => {
    clearSearchForJump();
    baseHandleJumpAppendix();
  }, [baseHandleJumpAppendix, clearSearchForJump]);

  /**
   * Effect: 現在ページを会社・傷病ペアごとに保存する
   * ペア切り替え直後は旧ペアのページ番号が一時的に残るため保存をスキップする
   */
  useEffect(() => {
    if (!activePairKey) return;
    const previousPairKey = previousPairKeyRef.current;
    const isPairSwitching = !!previousPairKey && previousPairKey !== activePairKey;
    if (isPairSwitching) return;
    savePairPageState(activePairKey, currentPage);
  }, [activePairKey, currentPage, savePairPageState]);

  /**
   * Effect: 会社・傷病ペア切り替え時にページと検索状態を復元する
   * ペア変更時は検索状態をクリアし、保存済みページがあればそのページへ移動する
   */
  useEffect(() => {
    if (!activePairKey) return;

    const previousPairKey = previousPairKeyRef.current;
    const isPairSwitched = !!previousPairKey && previousPairKey !== activePairKey;
    if (isPairSwitched) {
      baseHandleClearSearch();
      pendingAutoSearchRef.current = null;
      setIsSearchOpen(false);
    }

    previousPairKeyRef.current = activePairKey;

    const savedPage = pageStateByPairRef.current[activePairKey];
    if (Number.isFinite(savedPage) && savedPage > 0 && savedPage !== currentPage) {
      setCurrentPage(savedPage);
    }

    if (isPairSwitched || !previousPairKey) {
      pendingScrollRestorePairRef.current = activePairKey;
    }
  }, [
    activePairKey,
    baseHandleClearSearch,
    setCurrentPage,
    currentPage,
  ]);

  /**
   * Effect: 検索スコープ切り替え時に検索文字列を復元する
   * 保存済み検索語がある場合はPDF読み込み完了後の自動再検索を予約する
   */
  useEffect(() => {
    if (!activeSearchScopeKey || !activePairKey) return;

    const previousSearchScopeKey = previousSearchScopeKeyRef.current;
    const isScopeSwitched = !!previousSearchScopeKey && previousSearchScopeKey !== activeSearchScopeKey;
    previousSearchScopeKeyRef.current = activeSearchScopeKey;

    if (!isScopeSwitched) return;

    const restoredSearchText = searchTextByScopeRef.current[activeSearchScopeKey] ?? '';
    baseHandleClearSearch();
    setIsSearchOpen(false);

    if (restoredSearchText) {
      setSearchText(restoredSearchText);
      pendingAutoSearchRef.current = {
        pairKey: activePairKey,
        scopeKey: activeSearchScopeKey,
        query: restoredSearchText,
      };
    } else {
      pendingAutoSearchRef.current = null;
    }
  }, [
    activePairKey,
    activeSearchScopeKey,
    baseHandleClearSearch,
    setSearchText,
  ]);

  /**
   * Effect: 予約済みの自動再検索を実行する
   * PDFドキュメントと検索文字列が揃ってから検索を実行する
   */
  useEffect(() => {
    const pendingAutoSearch = pendingAutoSearchRef.current;
    if (!pendingAutoSearch) return;
    if (pendingAutoSearch.pairKey !== activePairKey) return;
    if (pendingAutoSearch.scopeKey !== activeSearchScopeKey) return;
    if (loadedDocumentPairRef.current !== activePairKey) return;
    if (!pdfDocument) return;
    if (searchText !== pendingAutoSearch.query) return;
    if (isSearching) return;

    pendingAutoSearchRef.current = null;
    handleSearchWithPersist();
  }, [activePairKey, activeSearchScopeKey, pdfDocument, searchText, isSearching, handleSearchWithPersist]);

  /**
   * Effect: PDFスクロール位置を会社・傷病ペアごとに保存する
   * スクロールイベントを監視し、タブ切り替え後の表示位置復元に使用する
   */
  useEffect(() => {
    const container = pdfScrollContainerRef.current;
    if (!container || !activePairKey) return undefined;

    const handleScroll = () => {
      scrollStateByPairRef.current[activePairKey] = {
        top: container.scrollTop,
        left: container.scrollLeft,
      };
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [activePairKey, pdfFile]);
  /**
   * Effect: PDF読み込みトリガー
   * blobNameとdetailResultが揃ったらPDFを読み込む
   */
  useEffect(() => {
    if (blobName && detailResult) {
      loadPdfDocument(blobName, detailResult);
    }
  }, [blobName, detailResult, loadPdfDocument]);

  /**
   * react-pdfコールバックラッパー
   * サブフックのコールバックをラップして追加処理を行う
   */

  /**
   * ドキュメント読み込み成功コールバック
   * 総ページ数を設定する
   */
  const onDocumentLoadSuccess = useCallback((result) => {
    pdfDocumentLoadSuccess(result, setTotalPages);
    loadedDocumentPairRef.current = activePairKey;
  }, [pdfDocumentLoadSuccess, setTotalPages, activePairKey]);

  /**
   * ページ読み込み成功コールバック
   */
  const onPageLoadSuccess = useCallback((page) => {
    pdfPageLoadSuccess(page);
  }, [pdfPageLoadSuccess]);

  /**
   * ページ描画成功コールバック
   * スクロール可能なサイズが確定した後に保存位置を復元する
   */
  const onPageRenderSuccess = useCallback(() => {
    const restorePairKey = pendingScrollRestorePairRef.current;
    if (!restorePairKey || restorePairKey !== activePairKey) return;

    requestAnimationFrame(() => {
      restorePairScrollState(restorePairKey);
      pendingScrollRestorePairRef.current = null;
    });
  }, [activePairKey, restorePairScrollState]);

  /**
   * ページ読み込みエラーコールバック
   * 現在のページ番号をエラーメッセージに含める
   */
  const onPageLoadError = useCallback((error) => {
    pdfPageLoadError(error, currentPage);
  }, [pdfPageLoadError, currentPage]);

  /**
   * PDF読み込みをリトライする
   * 状態をリセットしてから再読み込みを実行
   */
  const handleRetry = useCallback(() => {
    resetPdfState();
    setTotalPages(0);
    setCurrentPage(1);
    setIsRendering(false);
    setTimeout(() => {
      loadPdfDocument(blobName, detailResult);
    }, 100);
  }, [resetPdfState, setTotalPages, setCurrentPage, setIsRendering, loadPdfDocument, blobName, detailResult]);

  /**
   * 検索画面に戻る
   */
  const handleBackToSearch = useCallback(() => {
    navigate('/solicitor/insurance/system/search');
  }, [navigate]);

  /**
   * ウィンドウを閉じる
   * 新しいタブで開かれた場合に使用
   */
  const handleClose = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.close();
    }
  }, []);

  const handleActivateCompany = useCallback((companyName) => {
    saveActivePairState();
    baseHandleClearSearch();
    setIsSearchOpen(false);
    activateCompany?.(companyName);
  }, [activateCompany, baseHandleClearSearch, saveActivePairState, setIsSearchOpen]);

  const handleActivateDisease = useCallback((companyName, diseaseKey) => {
    saveActivePairState();
    baseHandleClearSearch();
    setIsSearchOpen(false);
    activateDisease?.(companyName, diseaseKey);
  }, [activateDisease, baseHandleClearSearch, saveActivePairState, setIsSearchOpen]);

  /**
   * 戻り値
   */
  return {
    // 状態: ローディング・エラー
    loading,
    showErrorPage,
    errorMessage,
    isRendering,

    // 状態: PDF表示
    pdfFile,
    pdfOptions,
    isLandscape,
    zoomLevel,
    currentPage,
    totalPages,

    // 状態: ハイライト
    highlightsPolygon,

    // 状態: ドキュメント情報
    documentInfo,
    isDocInfoAccordionOpen,

    // 状態: フィードバック
    currentPopup,
    isFeedbackSubmitting,
    currentQueryId,
    detailResult,
    groupedResults,
    activeCompany,
    activeCompanyResults,
    selectedDiseaseByCompany,

    // 状態: ジャンプボタン
    activeJumpButton,
    canJumpDisease,
    canJumpContents,
    canJumpLegend,
    canJumpAppendix,

    // 状態: レスポンシブ
    isMobile,

    // refs
    highlightCanvasRef,
    pdfContainerRef,
    pdfScrollContainerRef,

    // ハンドラ: ズーム
    handleZoomIn,
    handleZoomOut,

    // ハンドラ: ページ移動
    handlePageChange,
    handlePrevPage,
    handleNextPage,
    pageInputValue,
    handleChangePageInput,
    handlePageInputKeyDown,
    handlePageInputBlur,
    setCurrentPage,

    // ハンドラ: ジャンプ
    handleJumpDisease,
    handleJumpContents,
    handleJumpLegends,
    handleJumpAppendix,

    // ハンドラ: フィードバック
    handleFeedbackClose,
    handleGoodFeedback,
    handleBadFeedback,
    handleFeedbackSubmit,
    handleFeedbackBack,

    // ハンドラ: 画面操作
    handleRetry,
    handleBackToSearch,
    toggleDocInfoAccordion,
    handleClose,
    activateCompany: handleActivateCompany,
    activateDisease: handleActivateDisease,

    // react-pdfコールバック
    onDocumentLoadSuccess,
    onDocumentLoadError,
    onPageLoadSuccess,
    onPageRenderSuccess,
    onPageLoadError,

    // キーワード検索
    searchText,
    setSearchText,
    searchResults,
    currentSearchIndex,
    isSearching,
    searchError,
    isSearchOpen,
    setIsSearchOpen,
    handleSearch: handleSearchWithPersist,
    handleNextSearchResult,
    handlePrevSearchResult,
    handleClearSearch: handleClearSearchByUser,
    customTextRenderer,
    onGetTextSuccess,
    onTextLayerRenderSuccess,
    pdfDocument,
  };
};

export default useInsuranceHikiukeDetail;
